import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RenderModule } from 'holocron';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import { inavMarketsDetails } from './InavConfigs';
import InavLite from './InavLite';
import { GLOBAL_HEADER_NAME, INAV_VARIANTS } from '../../utils/constants';

const inavVariants = {
  [INAV_VARIANTS.CONTROL]: RenderModule,
  [INAV_VARIANTS.INAV_VAR_A]: InavLite,
};

const LoadHeader = ({
  type,
  actSetInavData,
  isExperiment,
  showExitModal,
  showExitButtonInHeader = true,
  modalProps = {},
  ...props
}) => {
  const Component = inavVariants[type];
  const [modalData, setModalData] = useState({
    show: showExitModal,
    showExitButtonInHeader,
    modalProps,
  });

  // istanbul ignore next
  const handleQuitModal = useCallback(
    (show, modalProp, showLeaveBtnInHeader) => {
      // Filtering to prevent undefined overrides from modalProp
      const filteredModalProps = Object.fromEntries(
        Object.entries(modalProp || {}).filter(([, value]) => value !== undefined)
      );

      setModalData((prev) => ({
        ...prev,
        modalProps: { ...prev?.modalProps, ...filteredModalProps },
        show,
        showExitButtonInHeader: showLeaveBtnInHeader,
      }));
    }, []);

  useEffect(() => {
    if (actSetInavData) {
      actSetInavData({ isExperiment, type });
    }
    window.axpRootModule = {};
    window.axpRootModule.handleQuitModal = handleQuitModal;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally scoped to isExperiment; actSetInavData/handleQuitModal/type are stable per mount
  }, [isExperiment]);

  return (
    <Component
      // eslint-disable-next-line react/jsx-props-no-spreading -- spreads a computed prop set that differs between control and experiment variants
      {...(type === INAV_VARIANTS.CONTROL
        ? { moduleName: GLOBAL_HEADER_NAME } : { ...props, modalData, actSetInavData })}
    />
  );
};

LoadHeader.propTypes = {
  type: PropTypes.string,
  isExperiment: PropTypes.bool,
  actSetInavData: PropTypes.func,
  showExitModal: PropTypes.bool,
  showExitButtonInHeader: PropTypes.bool,
  modalProps: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool])
  ),
};

const RenderInavHeader = ({
  locale,
  childModuleProps = {},
  actSetInavData,
  languageData,
}) => {
  const marketConfig = inavMarketsDetails[locale];
  if (!marketConfig) {
    return <LoadHeader actSetInavData={actSetInavData} type={INAV_VARIANTS.CONTROL} />;
  }

  const {
    inavLiteJourney,
    ignoreInavLiteModules,
    props,
  } = marketConfig;
  const { journey, moduleName: childModuleName } = childModuleProps;

  const showGlobalHeader = !Object.keys(inavLiteJourney)?.includes(journey)
    || ignoreInavLiteModules?.includes(childModuleName);

  const { experimentDetails } = inavLiteJourney?.[journey] || {};

  const extraProp = inavLiteJourney?.[journey]?.hideExitBtnOnLoad
    ? { showExitButtonInHeader: false } : {};

  if (showGlobalHeader || !experimentDetails) {
    return (
      <LoadHeader
        actSetInavData={actSetInavData}
        languageData={languageData}
        type={showGlobalHeader ? INAV_VARIANTS.CONTROL : INAV_VARIANTS.INAV_VAR_A}
        // eslint-disable-next-line react/jsx-props-no-spreading -- market-specific override props from config
        {...props}
        // eslint-disable-next-line react/jsx-props-no-spreading -- optional per-market override to hide the exit button
        {...extraProp}
      />
    );
  }
  const experimentName = experimentDetails?.experimentName;

  const variantsList = experimentDetails?.variants;

  return (
    <Experiment
      experimentName={experimentName}
      defaultVariant={experimentDetails?.defaultVariant}
      dimension={experimentDetails?.dimensionName}
      LoadingComponent={React.Fragment}
    >
      {variantsList?.map((itm) => (
        <Variant key={itm} variant={itm}>
          <LoadHeader
            languageData={languageData}
            actSetInavData={actSetInavData}
            isExperiment={true}
            type={itm}
            // eslint-disable-next-line react/jsx-props-no-spreading -- market-specific override props from config
            {...props}
          />
        </Variant>
      ))}
    </Experiment>
  );
};

RenderInavHeader.propTypes = {
  locale: PropTypes.string,
  location: PropTypes.objectOf(PropTypes.string),
  childModuleProps: PropTypes.objectOf(PropTypes.string),
  prefetchExperiments: PropTypes.objectOf(PropTypes.string),
  actSetInavData: PropTypes.func,
  languageData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ).isRequired,
};

export default React.memo(RenderInavHeader);
